import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { checkCrmAccess } from '@/services/crmClient';
import { CRM_API_URL } from '@/api/crmAxios';
import { getStoredUser } from '@/utils/authStorage';
import { ErrorPanel, Skeleton } from '@/components/UI/DataState';

/**
 * Wraps screens whose data lives on the CRM backend (telecom, reviews,
 * LOB templates, field options). Checks once that this console login is
 * accepted there before showing the screen.
 *
 * The CRM accepts a console login only when the same email is an active CRM
 * superadmin. Without that, every call would 401 and the screen would show
 * confusing per-widget errors, so we explain it once here.
 */
let cached = null; // 'ok' once confirmed for this page load

export default function CrmAccessGate({ title, children }) {
  const [state, setState] = useState(cached === 'ok' ? 'ok' : 'checking');
  const [error, setError] = useState(null);

  const check = useCallback(async () => {
    setState('checking');
    setError(null);
    try {
      await checkCrmAccess();
      cached = 'ok';
      setState('ok');
    } catch (err) {
      setError(err);
      const status = err?.response?.status;
      setState(status === 401 || status === 403 ? 'denied' : 'error');
    }
  }, []);

  useEffect(() => {
    if (cached !== 'ok') check();
  }, [check]);

  if (state === 'ok') return children;

  return (
    <div className="py-4 space-y-4" data-testid="crm-access-gate">
      {title && <h1 className="text-xl font-bold text-primary-black dark:text-white">{title}</h1>}
      {state === 'checking' && <Skeleton rows={5} />}
      {state === 'error' && <ErrorPanel error={error} what="data from the CRM backend" onRetry={check} />}
      {state === 'denied' && (
        <div role="alert" className="rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/20 p-5">
          <div className="flex items-start gap-3">
            <Icon icon="solar:shield-warning-linear" className="w-6 h-6 text-amber-600 flex-shrink-0" aria-hidden="true" />
            <div className="text-sm text-amber-900 dark:text-amber-200 space-y-2">
              <p className="font-semibold">This screen needs CRM superadmin access</p>
              <p>
                Its data lives on the CRM backend ({CRM_API_URL.replace(/^https?:\/\//, '')}), which accepts your console
                login only if <strong>{getStoredUser()?.email || 'your email'}</strong> is also an active CRM superadmin.
              </p>
              <p>Create that CRM superadmin (or set this user's CRM role to superadmin), then retry.</p>
              <button
                type="button"
                onClick={check}
                className="mt-1 min-h-[40px] px-4 rounded-lg border border-amber-300 dark:border-amber-800 bg-white dark:bg-gray-800 font-medium hover:bg-amber-100 dark:hover:bg-amber-900/40"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
